import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDesignStore from '../../store/designStore';
import useProjectStore from '../../store/projectStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';

const initialForm = {
  project_id: '',
  mode: 'heating',
  tube_side_flow_rate_m3h: '',
  tube_side_inlet_temp_c: '',
  tube_side_outlet_temp_c: '',
  tube_fluid_density_kgm3: '997',
  tube_fluid_specific_heat_kj_kgk: '4.186',
  steam_pressure_barg: '4',
  chilled_water_supply_temp_c: '7',
  chilled_water_return_temp_c: '12',
  overall_heat_transfer_coefficient_w_m2k: '',
  tube_internal_diameter_mm: '16',
  tube_wall_thickness_mm: '1.6',
  tube_length_m: '1',
  tube_pitch_type: 'triangular',
  tube_passes: '1',
  shell_bundle_clearance_mm: '50',
  tube_nozzle_velocity_m_s: '2.5',
  shell_liquid_nozzle_velocity_m_s: '2.5',
  shell_steam_nozzle_velocity_m_s: '30',
};

const HeatExchangerCalculatorPage = () => {
  const [form, setForm] = useState(initialForm);
  const [showGeometry, setShowGeometry] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const { result, isCalculating, isSubmitting, error, calculate, saveCalculation, clearResult } = useDesignStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    clearResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const buildInputs = () => {
    if (!form.tube_side_flow_rate_m3h || !form.tube_side_inlet_temp_c || !form.tube_side_outlet_temp_c) {
      showToast('Tube-side flow rate, inlet, and outlet temperature are required', 'error');
      return null;
    }
    return {
      mode: form.mode,
      tube_side_flow_rate_m3h: Number(form.tube_side_flow_rate_m3h),
      tube_side_inlet_temp_c: Number(form.tube_side_inlet_temp_c),
      tube_side_outlet_temp_c: Number(form.tube_side_outlet_temp_c),
      tube_fluid_density_kgm3: Number(form.tube_fluid_density_kgm3),
      tube_fluid_specific_heat_kj_kgk: Number(form.tube_fluid_specific_heat_kj_kgk),
      steam_pressure_barg: Number(form.steam_pressure_barg),
      chilled_water_supply_temp_c: Number(form.chilled_water_supply_temp_c),
      chilled_water_return_temp_c: Number(form.chilled_water_return_temp_c),
      overall_heat_transfer_coefficient_w_m2k:
        form.overall_heat_transfer_coefficient_w_m2k === '' ? null : Number(form.overall_heat_transfer_coefficient_w_m2k),
      tube_internal_diameter_mm: Number(form.tube_internal_diameter_mm),
      tube_wall_thickness_mm: Number(form.tube_wall_thickness_mm),
      tube_length_m: Number(form.tube_length_m),
      tube_pitch_type: form.tube_pitch_type,
      tube_passes: Number(form.tube_passes),
      shell_bundle_clearance_mm: Number(form.shell_bundle_clearance_mm),
      tube_nozzle_velocity_m_s: Number(form.tube_nozzle_velocity_m_s),
      shell_liquid_nozzle_velocity_m_s: Number(form.shell_liquid_nozzle_velocity_m_s),
      shell_steam_nozzle_velocity_m_s: Number(form.shell_steam_nozzle_velocity_m_s),
    };
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    const inputs = buildInputs();
    if (!inputs) return;
    await calculate('heat_exchanger', inputs);
  };

  const handleSave = async () => {
    if (!result) return;
    const payload = {
      calculator_type: 'heat_exchanger',
      project_id: form.project_id ? Number(form.project_id) : null,
      created_by_id: user?.id || null,
      client_data: result.inputs,
      calculation_outputs: result.outputs,
    };
    const saveResult = await saveCalculation(payload);
    if (saveResult.success) {
      showToast('Calculation saved as draft', 'success');
      navigate('/design/calculations');
    } else {
      showToast(saveResult.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-1">Heat Exchanger (Shell &amp; Tube)</h1>
        <p className="text-xs text-gray-500 mb-6">
          LMTD sizing method. Standard/textbook formulas - still needs engineer review before
          a result is trusted for a build. See "Assumptions" below the result for what's an
          estimate vs. a firm number.
        </p>

        <div className="glass-card p-6">
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
              <select
                value={form.project_id}
                onChange={handleChange('project_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Not linked to a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duty *</label>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={form.mode === 'heating'}
                    onChange={() => setForm((f) => ({ ...f, mode: 'heating' }))}
                  />
                  Heating (shell-side steam)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={form.mode === 'cooling'}
                    onChange={() => setForm((f) => ({ ...f, mode: 'cooling' }))}
                  />
                  Cooling (shell-side chilled water)
                </label>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tube-Side Flow (m³/hr) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.tube_side_flow_rate_m3h}
                  onChange={handleChange('tube_side_flow_rate_m3h')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inlet Temp (°C) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.tube_side_inlet_temp_c}
                  onChange={handleChange('tube_side_inlet_temp_c')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Outlet Temp (°C) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.tube_side_outlet_temp_c}
                  onChange={handleChange('tube_side_outlet_temp_c')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>

            {form.mode === 'heating' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Steam Supply Pressure (barg)</label>
                <input
                  type="number"
                  step="any"
                  value={form.steam_pressure_barg}
                  onChange={handleChange('steam_pressure_barg')}
                  className="w-48 border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Default: plant standard 4 barg"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chilled Water Supply (°C)</label>
                  <input
                    type="number"
                    step="any"
                    value={form.chilled_water_supply_temp_c}
                    onChange={handleChange('chilled_water_supply_temp_c')}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chilled Water Return (°C)</label>
                  <input
                    type="number"
                    step="any"
                    value={form.chilled_water_return_temp_c}
                    onChange={handleChange('chilled_water_return_temp_c')}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Overall U (W/m²K)</label>
              <input
                type="number"
                step="any"
                value={form.overall_heat_transfer_coefficient_w_m2k}
                onChange={handleChange('overall_heat_transfer_coefficient_w_m2k')}
                className="w-48 border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Blank = typical published default"
              />
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowGeometry((v) => !v)}
                className="text-xs font-semibold text-komal-blue hover:underline"
              >
                {showGeometry ? '− Hide' : '+ Show'} tube &amp; shell geometry (defaults: 16mm ID / 1.6mm wall / 1m tubes, triangular pitch, 1 pass)
              </button>

              {showGeometry && (
                <div className="grid grid-cols-3 gap-4 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tube Internal Dia (mm)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.tube_internal_diameter_mm}
                      onChange={handleChange('tube_internal_diameter_mm')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tube Wall Thickness (mm)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.tube_wall_thickness_mm}
                      onChange={handleChange('tube_wall_thickness_mm')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tube Length (m)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.tube_length_m}
                      onChange={handleChange('tube_length_m')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tube Pitch</label>
                    <select
                      value={form.tube_pitch_type}
                      onChange={handleChange('tube_pitch_type')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="triangular">Triangular</option>
                      <option value="square">Square</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tube Passes</label>
                    <select
                      value={form.tube_passes}
                      onChange={handleChange('tube_passes')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      {[1, 2, 4, 6, 8].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Bundle-to-Shell Clearance (mm)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.shell_bundle_clearance_mm}
                      onChange={handleChange('shell_bundle_clearance_mm')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tube-Side Density (kg/m³)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.tube_fluid_density_kgm3}
                      onChange={handleChange('tube_fluid_density_kgm3')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tube-Side Cp (kJ/kg·K)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.tube_fluid_specific_heat_kj_kgk}
                      onChange={handleChange('tube_fluid_specific_heat_kj_kgk')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tube Nozzle Velocity (m/s)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.tube_nozzle_velocity_m_s}
                      onChange={handleChange('tube_nozzle_velocity_m_s')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  {form.mode === 'heating' ? (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Shell Steam Nozzle Velocity (m/s)</label>
                      <input
                        type="number"
                        step="any"
                        value={form.shell_steam_nozzle_velocity_m_s}
                        onChange={handleChange('shell_steam_nozzle_velocity_m_s')}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Shell Liquid Nozzle Velocity (m/s)</label>
                      <input
                        type="number"
                        step="any"
                        value={form.shell_liquid_nozzle_velocity_m_s}
                        onChange={handleChange('shell_liquid_nozzle_velocity_m_s')}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="submit"
                disabled={isCalculating}
                className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
              >
                {isCalculating ? 'Calculating...' : 'Calculate'}
              </button>
            </div>
          </form>
        </div>

        {result && (
          <div className="glass-card p-6 mt-4">
            <h2 className="font-semibold text-gray-900 mb-4">Result</h2>

            {result.outputs.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 space-y-1">
                {result.outputs.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-800">⚠ {w}</p>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <Stat label="Duty (Rating)" value={`${result.outputs.duty_kw} kW`} />
              <Stat label="Duty (kcal/hr)" value={result.outputs.duty_kcal_hr.toLocaleString()} />
              <Stat label="LMTD" value={`${result.outputs.lmtd_c} °C`} />
              <Stat label="Overall U" value={`${result.outputs.overall_u_w_m2k} W/m²K${result.outputs.u_value_is_default ? ' (default)' : ''}`} />
              <Stat label="Required Area" value={`${result.outputs.required_area_m2} m²`} />
              <Stat label="Number of Tubes" value={result.outputs.num_tubes} highlight />
              <Stat label="Bundle Diameter" value={`${result.outputs.bundle_diameter_mm} mm`} />
              <Stat label="Shell Inside Diameter" value={`${result.outputs.shell_inside_diameter_mm} mm`} highlight />
              {result.outputs.utility_type === 'steam' ? (
                <>
                  <Stat label="Steam Sat. Temp" value={`${result.outputs.steam_saturation_temp_c} °C`} />
                  <Stat label="Steam Required" value={`${result.outputs.utility_flow_kg_hr} kg/hr`} highlight />
                </>
              ) : (
                <Stat label="Chilled Water Required" value={`${result.outputs.utility_flow_m3_hr} m³/hr`} highlight />
              )}
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-4 mb-2">Nozzle Sizing</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <Stat label="Tube-Side Nozzle" value={`${result.outputs.tube_nozzle_diameter_mm} mm`} highlight />
              <Stat label="Tube Nozzle Velocity" value={`${result.outputs.tube_nozzle_velocity_m_s} m/s`} />
              <Stat
                label={`Shell-Side Nozzle (${result.outputs.utility_type === 'steam' ? 'steam' : 'chilled water'})`}
                value={`${result.outputs.shell_nozzle_diameter_mm} mm`}
                highlight
              />
              <Stat label="Shell Nozzle Velocity" value={`${result.outputs.shell_nozzle_velocity_m_s} m/s`} />
              {result.outputs.utility_type === 'steam' && (
                <>
                  <Stat label="Steam Specific Volume" value={`${result.outputs.steam_specific_volume_m3_kg} m³/kg`} />
                  <Stat label="Steam Volumetric Flow" value={`${result.outputs.steam_volumetric_flow_m3_hr} m³/hr`} />
                </>
              )}
            </div>

            {(result.outputs.calculation_steps || result.outputs.assumptions) && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className="text-xs font-semibold text-komal-blue hover:underline mb-3"
                >
                  {showDetails ? '− Hide calculation & assumptions' : '+ Show calculation & assumptions'}
                </button>

                {showDetails && (
                  <div className="space-y-4">
                    {result.outputs.calculation_steps?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Calculation Steps
                        </h3>
                        <div className="bg-gray-50 rounded border border-gray-100">
                          <ScrollX>
                          <table className="w-full text-xs">
                            <tbody>
                              {result.outputs.calculation_steps.map((step, i) => (
                                <tr key={i} className="border-b border-gray-100 last:border-0">
                                  <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">{step.step}</td>
                                  <td className="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">{step.formula}</td>
                                  <td className="px-3 py-2 text-gray-900 font-mono">{step.result}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </ScrollX>
                        </div>
                      </div>
                    )}

                    {result.outputs.assumptions?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Assumptions
                        </h3>
                        <ul className="list-disc list-inside space-y-1">
                          {result.outputs.assumptions.map((a, i) => (
                            <li key={i} className="text-xs text-gray-600">{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={() => clearResult()}
                className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
              >
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`font-semibold ${highlight ? 'text-komal-blue text-lg' : 'text-gray-900'}`}>{value}</div>
  </div>
);

export default HeatExchangerCalculatorPage;
